import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  ZapIcon,
  ShieldCheckIcon,
  TrendingUpIcon,
  PlayCircleIcon,
  StorefrontIcon,
  ShoppingCartIcon,
  PackageBoxIcon,
  GrowthChartIcon,
  CheckIcon,
  UsersGroupIcon,
  CreditCardIcon,
  TruckIcon,
  BarChart3Icon,
  HeadphonesIcon,
  IdCardIcon,
  FileTextIcon,
  SparklesIcon
} from "../components/Icons";

import jordanImg from "../assets/hero-jordan.jpg";
import yeezyImg from "../assets/hero-yeezy.jpg";
import dunkImg from "../assets/hero-dunk.jpg";

const FAQItem = ({ q, a, isOpen, onToggle }) => (
  <div className={`modern-faq-item ${isOpen ? "open" : ""}`}>
    <button
      className="modern-faq-question"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span className="faq-q-text">{q}</span>
      <span className="faq-toggle-icon">
        <span className="faq-toggle-plus">+</span>
      </span>
    </button>
    <div className={`modern-faq-answer ${isOpen ? "open" : ""}`}>
      <div className="modern-faq-answer-content">{a}</div>
    </div>
  </div>
);

function PublicHome() {
  const [sales, setSales] = useState(25000);
  const [openFaq, setOpenFaq] = useState(0);
  const [dashboardTab, setDashboardTab] = useState("30d");
  const [agendaTab, setAgendaTab] = useState("what-we-provide"); // "what-we-provide" | "how-we-help" | "our-mission"

  const basicFee = Math.round(sales * 0.1);
  const growthFee = Math.round(300 + sales * 0.05);
  const proFee = 499;
  const savings = basicFee - proFee;

  const faqs = [
    {
      q: "Who can sell on IM Online?",
      a: "Anyone interested in selling sneakers, footwear, and fashion apparel can register. Individual sellers, boutique store owners, wholesalers, and established footwear brands are all welcome. You do not need prior e-commerce experience to get started."
    },
    {
      q: "What documents are required to get started?",
      a: "Getting verified requires only basic identity and business proof: Aadhaar Card, PAN Card, active phone number, email, and bank account details for payouts. The verification process is 100% digital."
    },
    {
      q: "Do I need GST registration to start selling?",
      a: "GST requirements depend on your business structure, product category, and annual turnover. For many entry-level footwear sellers, onboarding can begin right away with basic identity documents, and our seller onboarding team will assist you with tax compliance as your sales scale."
    },
    {
      q: "How does the pricing and commission model work?",
      a: "Our Basic Plan is 100% free with no monthly subscription (10% commission). Our Growth Plan is ₹300/month (5% commission), and our flagship Pro Plan is ₹499/month with 0% marketplace commission so you keep 100% of your product revenue."
    },
    {
      q: "How do I receive and fulfill customer orders?",
      a: "When a customer places an order, you receive instant email and dashboard notifications. Pack the product in standard packaging, and use our integrated logistics pickup or generate shipping labels directly from your seller dashboard."
    },
    {
      q: "When and how do I receive payouts?",
      a: "Payments from customer purchases are processed securely and credited directly to your registered bank account on a regular automated payout cycle with full settlement transparency in your financial reports."
    },
    {
      q: "How long does seller approval take?",
      a: "Our seller verification team reviews applications within 24 to 48 business hours. Once verified, you can immediately begin listing footwear products and publishing your storefront."
    },
    {
      q: "Can I upgrade or cancel my subscription plan at any time?",
      a: "Yes. You can switch between Basic, Growth, and Pro plans at any time directly from the Subscription section in your Seller Center dashboard with no penalties or lock-in periods."
    }
  ];

  const featureCards = [
    {
      icon: <StorefrontIcon size={24} color="#a85050" />,
      title: "List Your Products",
      desc: "Easily add and manage your sneaker & footwear products with sizes and stock."
    },
    {
      icon: <ShoppingCartIcon size={24} color="#a85050" />,
      title: "Reach More Customers",
      desc: "Get discovered by thousands of buyers across the platform every day."
    },
    {
      icon: <PackageBoxIcon size={24} color="#a85050" />,
      title: "Receive Orders",
      desc: "Manage orders, shipping, and real-time package tracking seamlessly."
    },
    {
      icon: <GrowthChartIcon size={24} color="#a85050" />,
      title: "Grow & Scale",
      desc: "Use our tools and insights to grow your business with 0% commission options."
    }
  ];

  const steps = [
    {
      num: "01",
      title: "Create Your Account",
      desc: "Sign up in 2 minutes with your mobile number, email, and business name.",
      badge: "Instant Signup"
    },
    {
      num: "02",
      title: "Add Your Products",
      desc: "Upload footwear photos, set sizing, define inventory counts, and set competitive prices.",
      badge: "Quick Bulk Upload"
    },
    {
      num: "03",
      title: "Start Receiving Orders",
      desc: "Buyers across India discover your listings, place orders, and pay securely.",
      badge: "Nationwide Reach"
    },
    {
      num: "04",
      title: "Grow & Scale",
      desc: "Fulfill orders, receive direct bank payouts, and unlock seller analytics.",
      badge: "0% Commission Plan"
    }
  ];

  const whySellFeatures = [
    {
      icon: <UsersGroupIcon size={26} color="#a85050" />,
      title: "Vast Footwear Buyer Network",
      desc: "Direct access to thousands of daily sneaker enthusiasts and active shoppers across India."
    },
    {
      icon: <ZapIcon size={26} color="#a85050" />,
      title: "Simple 2-Minute Onboarding",
      desc: "No confusing technical jargon or expensive developers. Get up and running in minutes."
    },
    {
      icon: <CreditCardIcon size={26} color="#a85050" />,
      title: "Fast, Secure Direct Payouts",
      desc: "Automated settlements sent straight to your verified bank account on weekly cycles."
    },
    {
      icon: <TruckIcon size={26} color="#a85050" />,
      title: "Easy Order Management",
      desc: "Integrated tracking, automated shipping labels, and doorstep order pickups nationwide."
    },
    {
      icon: <BarChart3Icon size={26} color="#a85050" />,
      title: "Actionable Seller Analytics",
      desc: "Real-time visibility into your revenue, fast-moving footwear sizes, and conversion rates."
    },
    {
      icon: <HeadphonesIcon size={26} color="#a85050" />,
      title: "Dedicated Seller Support",
      desc: "Our merchant success team is on standby to help you list, sell, and resolve issues fast."
    }
  ];

  // ── WHAT WE PROVIDE (6 CORE PILLARS) ──
  const whatWeProvide = [
    {
      icon: <CreditCardIcon size={28} color="#a85050" />,
      tag: "0% COMMISSION OPTION",
      title: "Keep Up to 100% of Your Profits",
      desc: "Big marketplaces take 25%–35% of every sale. With our flagship Pro plan (₹499/mo), you pay 0% marketplace commission so you keep every rupee of your product margin.",
      perk: "Zero profit-gouging guarantee"
    },
    {
      icon: <TruckIcon size={28} color="#a85050" />,
      tag: "DOORSTEP LOGISTICS",
      title: "Nationwide Pickups to 27,000+ Pin Codes",
      desc: "Integrated courier network via Delhivery, BlueDart, and Shiprocket. Generate shipping labels with 1 click; delivery personnel arrive directly at your shop counter.",
      perk: "Doorstep courier dispatch"
    },
    {
      icon: <ShieldCheckIcon size={28} color="#a85050" />,
      tag: "DIRECT BANK SETTLEMENTS",
      title: "Guaranteed Payouts with 0 Hidden Fees",
      desc: "Direct deposits sent straight to your verified Indian bank account on automated weekly settlement cycles with down-to-the-rupee ledger transparency.",
      perk: "100% transparent ledger"
    },
    {
      icon: <PackageBoxIcon size={28} color="#a85050" />,
      tag: "FOOTWEAR SPECIALIZED",
      title: "Shoe Sizing & Inventory Matrix",
      desc: "Built specifically for shoes & sneakers: Indian/UK sizing (UK 6 to 12), half-sizes, multi-angle HD photo galleries, and real-time inventory warnings.",
      perk: "UK/India sizing built-in"
    },
    {
      icon: <StorefrontIcon size={28} color="#a85050" />,
      tag: "YOUR OWN BRAND",
      title: "Custom Branded Online Storefront",
      desc: "Get your personalized store link (imonline.shop/store/your-shop), custom banner, brand story, and verified badge. Direct link to share on Instagram & WhatsApp.",
      perk: "Direct store URL & badge"
    },
    {
      icon: <HeadphonesIcon size={28} color="#a85050" />,
      tag: "1-ON-1 GUIDANCE",
      title: "Dedicated Human Merchant Partner",
      desc: "No automated bot loops. You get a real merchant success partner via WhatsApp & direct phone to help you shoot photos, write titles, and launch your listings.",
      perk: "Direct WhatsApp seller desk"
    }
  ];

  // ── HOW WE WILL HELP YOU (5-STAGE ROADMAP) ──
  const howWeHelpRoadmap = [
    {
      stage: "01",
      timeline: "DAY 1",
      badge: "Fast Digital KYC",
      title: "2-Minute Digital Registration",
      desc: "Sign up with your mobile number, store name, and basic Aadhaar & PAN details. No confusing paperwork. Our verification team approves genuine sellers in 24–48 hours."
    },
    {
      stage: "02",
      timeline: "DAY 2",
      badge: "Catalog Assistance",
      title: "Footwear Catalog & Photo Optimization",
      desc: "Upload photos from your smartphone. Our catalog specialists assist you in optimizing shoe titles, size charts, descriptions, and tags for maximum search conversion."
    },
    {
      stage: "03",
      timeline: "WEEK 1",
      badge: "Instant Exposure",
      title: "Nationwide Customer Discovery",
      desc: "Your sneakers go live across the IM Online marketplace and customer mobile app, highlighted in trending sneaker feeds, seasonal discounts, and verified merchant carousels."
    },
    {
      stage: "04",
      timeline: "ORDER DAY",
      badge: "Zero-Effort Dispatch",
      title: "1-Click Order Fulfillment & Doorstep Pickup",
      desc: "Get instant WhatsApp and dashboard notifications when orders roll in. Pack the shoe box, print shipping labels with 1 click, and hand over parcels to the courier at your door."
    },
    {
      stage: "05",
      timeline: "PAYOUT DAY",
      badge: "Profitable Scaling",
      title: "Direct Bank Payouts & Scaling to Lakhs",
      desc: "Payments are automatically credited to your bank account with complete transparency. Upgrade to Pro for 0% commission to pocket 100% of your retail margin."
    }
  ];

  // ── OUR AGENDA & VALUE PLEDGE COMPARISON ──
  const agendaComparison = [
    {
      feature: "Marketplace Commission",
      others: "25% to 35% cut on every product sold",
      imonline: "0% Commission on Pro Plan (Keep 100% of your margin)",
      highlight: true
    },
    {
      feature: "Store Identity & Branding",
      others: "Hidden behind anonymous marketplace catalog",
      imonline: "Your own branded storefront link, banner, and verified badge",
      highlight: true
    },
    {
      feature: "Order Logistics & Shipping",
      others: "Seller pays heavy fulfillment & warehousing fees",
      imonline: "Doorstep pickup from your shop counter to 27,000+ pin codes",
      highlight: false
    },
    {
      feature: "Merchant Support Desk",
      others: "Automated bots and endless support ticket queues",
      imonline: "Dedicated human account manager reachable via WhatsApp & phone",
      highlight: true
    },
    {
      feature: "Payout Settlements",
      others: "Delayed payouts with confusing deduction penalties",
      imonline: "Automated weekly bank settlements with zero surprise deductions",
      highlight: false
    },
    {
      feature: "Footwear Sizing System",
      others: "Generic sizes with high return rates",
      imonline: "Specialized UK/India sneaker sizing engine & size guides",
      highlight: false
    }
  ];

  return (
    <div className="landing-page-root">
      <SEO
        title="Sell on IM Online | 0% Commission Footwear & Fashion Seller Portal India"
        description="Become a seller on IM Online Seller Center. Sell footwear, sneakers, and fashion apparel across India. Enjoy 0% commission on Pro plan, instant payouts, and doorstep order pickups."
        keywords="sell shoes online india, footwear seller registration, sneaker vendor portal, sell sneakers online, footwear marketplace india, 0% commission ecommerce, become a seller im online, online shoe store merchant"
        canonical="https://vendor.imonline.shop/"
        ogTitle="Sell on IM Online - India's Sneaker & Footwear Merchant Portal"
        ogDescription="Start selling sneakers and fashion footwear online in 2 minutes. 0% commission options, automated weekly payouts, and nationwide customer reach."
        schema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": f.a
            }
          }))
        }}
      />
      <Navbar />

      {/* ========================================================================= */}
      {/* 1. HERO SECTION (2-COLUMN HERO MATCHING REFERENCE COMPOSITION) */}
      {/* ========================================================================= */}
      {/* ========================================================================= */}
      {/* 1. HERO SECTION (HIGH-CONVERTING FOOTWEAR SELLER SHOWCASE) */}
      {/* ========================================================================= */}
      <section className="modern-hero-section">
        <div className="container">
          <div className="hero-grid-row">
            {/* Left Column: Hero Content */}
            <div className="hero-col-left">
              {/* Badge */}
              <div className="hero-portal-badge">
                <span className="badge-pulse-dot" />
                <span className="badge-text">INDIA'S 0% COMMISSION FOOTWEAR PORTAL</span>
              </div>

              {/* Main Headline */}
              <h1 className="hero-main-headline">
                Start Selling Online.
                <br />
                <span className="hero-headline-accent">Keep 100%</span> Of Your Profits.
              </h1>

              {/* Supporting Copy */}
              <p className="hero-supporting-text">
                Join India's premier sneaker and footwear merchant marketplace. Sell to thousands of daily buyers with <strong>0% marketplace commission</strong>, automated weekly payouts, and doorstep courier pickups to 27,000+ pin codes.
              </p>

              {/* Action Buttons */}
              <div className="hero-cta-buttons">
                <Link to="/vendor/register" className="btn-hero-primary">
                  <span>Start Selling Free</span>
                  <span className="arrow-icon">&rarr;</span>
                </Link>
                <a href="#how-it-works" className="btn-hero-secondary">
                  <span>How It Works</span>
                  <span className="play-icon-circle">
                    <PlayCircleIcon size={18} color="#a85050" />
                  </span>
                </a>
              </div>

              {/* Trust Reassurance Chips */}
              <div className="hero-trust-chips-row">
                <span className="hero-reassurance-pill">
                  <CheckIcon size={13} color="#16a34a" /> Instant 2-Min KYC
                </span>
                <span className="hero-reassurance-pill">
                  <CheckIcon size={13} color="#16a34a" /> Doorstep Courier Dispatch
                </span>
                <span className="hero-reassurance-pill">
                  <CheckIcon size={13} color="#16a34a" /> 0% Commission on Pro
                </span>
              </div>

              {/* Trust & Value Props Grid */}
              <div className="hero-trust-grid">
                <div className="hero-trust-item">
                  <div className="hero-trust-icon-box">
                    <ZapIcon size={18} color="#a85050" />
                  </div>
                  <div className="hero-trust-item-text">
                    <strong>Easy Setup</strong>
                    <span>List shoes in 2 mins</span>
                  </div>
                </div>

                <div className="hero-trust-item">
                  <div className="hero-trust-icon-box">
                    <ShieldCheckIcon size={18} color="#a85050" />
                  </div>
                  <div className="hero-trust-item-text">
                    <strong>Direct Bank Payouts</strong>
                    <span>Automated weekly cycle</span>
                  </div>
                </div>

                <div className="hero-trust-item">
                  <div className="hero-trust-icon-box">
                    <TrendingUpIcon size={18} color="#a85050" />
                  </div>
                  <div className="hero-trust-item-text">
                    <strong>0% Commission</strong>
                    <span>Keep 100% margin</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Hero Visual Composition (Live Seller Portal Showcase) */}
            <div className="hero-col-right">
              <div className="hero-showcase-container">
                {/* Background Ambient Glows */}
                <div className="hero-bg-circle-glow" />
                <div className="hero-bg-circle-accent" />

                {/* Main Card: Live Seller Center Showcase */}
                <div className="hero-showcase-card">
                  {/* Card Header Strip */}
                  <div className="showcase-card-header">
                    <div className="showcase-header-left">
                      <span className="showcase-live-pulse" />
                      <span className="showcase-header-title">SELLER PORTAL &middot; LIVE DEMO</span>
                    </div>
                    <span className="showcase-header-badge">0% Commission Pro</span>
                  </div>

                  {/* Product Spotlight Unit */}
                  <div className="showcase-product-spotlight">
                    <div className="spotlight-thumb-wrap">
                      <img
                        src={jordanImg}
                        alt="Air Jordan 1 High OG"
                        className="spotlight-img"
                      />
                      <span className="spotlight-verified-badge">&#x2713; Verified</span>
                    </div>
                    <div className="spotlight-info-col">
                      <div className="spotlight-brand-tag">TRENDING FOOTWEAR</div>
                      <h3 className="spotlight-product-title">Air Jordan 1 High OG</h3>
                      <div className="spotlight-sizes-row">
                        <span className="size-chip active">UK 7</span>
                        <span className="size-chip active">UK 8</span>
                        <span className="size-chip active">UK 9</span>
                        <span className="size-chip active">UK 10</span>
                      </div>
                      <div className="spotlight-pricing-row">
                        <span className="spotlight-price">&#x20B9;8,499</span>
                        <span className="spotlight-margin-chip">&#x1F4B0; 100% Margin Kept</span>
                      </div>
                    </div>
                  </div>

                  {/* Real-Time Seller Stat Grid */}
                  <div className="showcase-stats-grid">
                    <div className="showcase-stat-tile">
                      <span className="showcase-stat-label">Total Net Revenue</span>
                      <div className="showcase-stat-num">&#x20B9;2,45,680</div>
                      <span className="showcase-stat-trend positive">&#x2191; +23.5% this month</span>
                    </div>
                    <div className="showcase-stat-tile">
                      <span className="showcase-stat-label">Orders Dispatched</span>
                      <div className="showcase-stat-num">1,245</div>
                      <span className="showcase-stat-trend neutral">&#x1F69A; Delhivery &amp; BlueDart</span>
                    </div>
                  </div>

                  {/* Live Order Activity Feed */}
                  <div className="showcase-orders-feed">
                    <div className="showcase-feed-header">
                      <span>Recent Orders (Live Sync)</span>
                      <span className="feed-status-dot">&#x25CF; Active</span>
                    </div>
                    <div className="showcase-order-row">
                      <div className="order-row-thumb">
                        <img src={yeezyImg} alt="Yeezy" />
                      </div>
                      <div className="order-row-info">
                        <span className="order-row-name">Yeezy Boost 350 (UK 8)</span>
                        <span className="order-row-dest">Dispatch to Bangalore</span>
                      </div>
                      <div className="order-row-right">
                        <span className="order-row-price">&#x20B9;7,999</span>
                        <span className="order-badge-transit">In Transit</span>
                      </div>
                    </div>

                    <div className="showcase-order-row">
                      <div className="order-row-thumb">
                        <img src={dunkImg} alt="Nike Dunk" />
                      </div>
                      <div className="order-row-info">
                        <span className="order-row-name">Nike Dunk Low (UK 10)</span>
                        <span className="order-row-dest">Dispatch to Mumbai</span>
                      </div>
                      <div className="order-row-right">
                        <span className="order-row-price">&#x20B9;6,499</span>
                        <span className="order-badge-delivered">Delivered</span>
                      </div>
                    </div>
                  </div>

                  {/* Footer Reassurance Strip */}
                  <div className="showcase-card-footer">
                    <div className="showcase-seller-avatar">&#x1F45F;</div>
                    <div className="showcase-seller-text">
                      <strong>Royal Footwear Hub</strong>
                      <span>Verified Merchant &middot; 4.9 &#x2605; (1,245 orders)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ========================================================================= */}
          {/* 2. FEATURE STRIP (4-COLUMN HIGH-IMPACT PILLARS) */}
          {/* ========================================================================= */}
          <div className="feature-strip-container">
            <div className="feature-strip-card">
              <div className="feature-strip-grid">
                {featureCards.map((item, idx) => (
                  <div key={idx} className="feature-strip-col">
                    <div className="feature-strip-icon-wrap">
                      {item.icon}
                    </div>
                    <div className="feature-strip-text">
                      <h4 className="feature-strip-title">{item.title}</h4>
                      <p className="feature-strip-desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 2.5 OUR SELLER AGENDA: WHAT WE PROVIDE & HOW WE WILL HELP YOU */}
      {/* ========================================================================= */}
      <section id="our-agenda" className="section-padded our-agenda-section">
        <div className="container">
          <div className="section-header-center">
            <span className="section-pill-tag">OUR SELLER AGENDA &amp; MISSION</span>
            <h2 className="section-title">
              Built for Indian Footwear Sellers &mdash; <span className="hero-headline-accent">100% Transparent</span>
            </h2>
            <p className="section-subtitle" style={{ maxWidth: "780px" }}>
              Traditional e-commerce platforms take 25% to 35% in commissions and hide your brand behind algorithms.
              Our agenda is 100% merchant-first: we give you enterprise-grade logistics, 0% commission plans, and dedicated human support so you keep your profits and grow your own footwear brand.
            </p>
          </div>

          {/* Interactive Navigation Switcher Tabs */}
          <div className="agenda-tabs-wrapper">
            <button
              type="button"
              className={`agenda-tab-btn ${agendaTab === "what-we-provide" ? "active" : ""}`}
              onClick={() => setAgendaTab("what-we-provide")}
            >
              <span className="agenda-tab-icon">&#x1F4BC;</span>
              <span className="agenda-tab-text">What We Offer</span>
            </button>

            <button
              type="button"
              className={`agenda-tab-btn ${agendaTab === "how-we-help" ? "active" : ""}`}
              onClick={() => setAgendaTab("how-we-help")}
            >
              <span className="agenda-tab-icon">&#x1F680;</span>
              <span className="agenda-tab-text">How We Help</span>
            </button>

            <button
              type="button"
              className={`agenda-tab-btn ${agendaTab === "our-mission" ? "active" : ""}`}
              onClick={() => setAgendaTab("our-mission")}
            >
              <span className="agenda-tab-icon">&#x1F6E1;&#xFE0F;</span>
              <span className="agenda-tab-text">Our Pledge</span>
            </button>
          </div>

          {/* ── TAB 1: WHAT WE PROVIDE YOU (6 PILLARS) ── */}
          {agendaTab === "what-we-provide" && (
            <div className="agenda-content-panel fade-in">
              <div className="agenda-pillars-grid">
                {whatWeProvide.map((item, idx) => (
                  <div key={idx} className="agenda-pillar-card">
                    <div className="agenda-pillar-top">
                      <div className="agenda-pillar-icon-box">{item.icon}</div>
                      <span className="agenda-pillar-tag">{item.tag}</span>
                    </div>
                    <h3 className="agenda-pillar-title">{item.title}</h3>
                    <p className="agenda-pillar-desc">{item.desc}</p>
                    <div className="agenda-pillar-perk">
                      <CheckIcon size={14} color="#16a34a" />
                      <span>{item.perk}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── TAB 2: HOW WE WILL HELP YOU (5-STAGE ROADMAP) ── */}
          {agendaTab === "how-we-help" && (
            <div className="agenda-content-panel fade-in">
              <div className="agenda-roadmap-list">
                {howWeHelpRoadmap.map((item, idx) => (
                  <div key={idx} className="agenda-roadmap-card">
                    <div className="agenda-roadmap-left">
                      <div className="agenda-roadmap-num">{item.stage}</div>
                      <span className="agenda-roadmap-timeline">{item.timeline}</span>
                    </div>
                    <div className="agenda-roadmap-center">
                      <div className="agenda-roadmap-header-line">
                        <h4 className="agenda-roadmap-title">{item.title}</h4>
                        <span className="agenda-roadmap-badge">{item.badge}</span>
                      </div>
                      <p className="agenda-roadmap-desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── TAB 3: OUR MISSION & PLEDGE COMPARISON ── */}
          {agendaTab === "our-mission" && (
            <div className="agenda-content-panel fade-in">
              {/* Mission Statement Hero Box */}
              <div className="agenda-mission-box">
                <div className="agenda-mission-icon-wrap">
                  <ShieldCheckIcon size={36} color="#ffffff" />
                </div>
                <div className="agenda-mission-text">
                  <h3 className="agenda-mission-headline">Our Founding Promise: Never Gouge the Merchant</h3>
                  <p className="agenda-mission-sub">
                    IM Online was founded on a simple observation: retail footwear shopkeepers and sneaker makers are the lifeblood of Indian commerce, yet traditional platforms extract 30%+ of your revenues while hiding your shop name. Our mission is to restore transparency, enable direct doorstep dispatch, and let you run your business on your own terms.
                  </p>
                </div>
              </div>

              {/* Comparison Table */}
              <div className="agenda-table-card">
                <div className="table-responsive">
                  <table className="agenda-comparison-table">
                    <thead>
                      <tr>
                        <th style={{ width: "32%" }}>Marketplace Feature</th>
                        <th style={{ width: "34%" }}>Traditional E-Commerce Portals</th>
                        <th style={{ width: "34%" }} className="im-col-head">IM Online Seller Center</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agendaComparison.map((row, idx) => (
                        <tr key={idx} className={row.highlight ? "row-highlighted" : ""}>
                          <td className="feature-cell">
                            <strong>{row.feature}</strong>
                          </td>
                          <td className="others-cell">
                            <span className="status-cross">✕</span> {row.others}
                          </td>
                          <td className="im-cell">
                            <span className="status-check">✓</span> <strong>{row.imonline}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Bottom Direct Assistance Banner */}
          <div className="agenda-bottom-banner">
            <div className="agenda-banner-left">
              <div className="agenda-banner-avatar">👨‍💼</div>
              <div>
                <h4 className="agenda-banner-title">Need guidance setting up your shop?</h4>
                <p className="agenda-banner-sub">
                  Our merchant onboarding team is available to assist you on WhatsApp: <strong>support@imonline.shop</strong>
                </p>
              </div>
            </div>
            <div className="agenda-banner-right">
              <Link to="/vendor/register" className="btn-hero-primary" style={{ padding: "12px 24px" }}>
                <span>Start Selling Free</span>
                <span className="arrow-icon">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. HOW IT WORKS (4-STEP PROCESS) */}
      {/* ========================================================================= */}
      <section id="how-it-works" className="section-padded bg-white">
        <div className="container">
          <div className="section-header-center">
            <span className="section-pill-tag">HOW IT WORKS</span>
            <h2 className="section-title">Start Selling in 4 Simple Steps</h2>
            <p className="section-subtitle">
              From free account registration to your first live customer order in under 48 hours.
            </p>
          </div>

          <div className="how-it-works-grid">
            {steps.map((step, idx) => (
              <div key={idx} className="step-card-modern">
                <div className="step-card-top">
                  <span className="step-card-number">{step.num}</span>
                  <span className="step-card-tag">{step.badge}</span>
                </div>
                <h3 className="step-card-title">{step.title}</h3>
                <p className="step-card-desc">{step.desc}</p>
                <div className="step-card-progress-indicator" />
              </div>
            ))}
          </div>

          <div className="how-it-works-cta-wrap">
            <Link to="/vendor/register" className="btn-primary btn-lg">
              Create Your Seller Account Now &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 4. WHY SELL WITH IM ONLINE */}
      {/* ========================================================================= */}
      <section id="why-sell" className="section-padded bg-subtle-warm">
        <div className="container">
          <div className="section-header-center">
            <span className="section-pill-tag">WHY SELL WITH US</span>
            <h2 className="section-title">Everything You Need to Scale Online</h2>
            <p className="section-subtitle">
              Purpose-built tools, nationwide logistics, and 0% commission plans for modern footwear merchants.
            </p>
          </div>

          <div className="why-sell-grid">
            {whySellFeatures.map((item, idx) => (
              <div key={idx} className="why-sell-card">
                <div className="why-sell-icon-container">
                  {item.icon}
                </div>
                <h4 className="why-sell-title">{item.title}</h4>
                <p className="why-sell-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 5. SELLER DASHBOARD PREVIEW SHOWCASE */}
      {/* ========================================================================= */}
      <section id="dashboard-preview" className="section-padded bg-white">
        <div className="container">
          <div className="section-header-center">
            <span className="section-pill-tag">SELLER DASHBOARD</span>
            <h2 className="section-title">A Command Center Built for Growth</h2>
            <p className="section-subtitle">
              Manage inventory, monitor real-time revenue, process orders, and track payouts in one intuitive dashboard.
            </p>
          </div>

          <div className="dashboard-preview-window">
            {/* Top Bar of the Dashboard Preview */}
            <div className="dash-window-header">
              <div className="dash-window-tabs">
                <span className="dash-window-dot red" />
                <span className="dash-window-dot yellow" />
                <span className="dash-window-dot green" />
                <span className="dash-window-title">IM Online Seller Center &mdash; Analytics &amp; Orders</span>
              </div>
              <div className="dash-period-selector">
                <button
                  className={`dash-period-btn ${dashboardTab === "7d" ? "active" : ""}`}
                  onClick={() => setDashboardTab("7d")}
                >
                  7 Days
                </button>
                <button
                  className={`dash-period-btn ${dashboardTab === "30d" ? "active" : ""}`}
                  onClick={() => setDashboardTab("30d")}
                >
                  30 Days
                </button>
                <button
                  className={`dash-period-btn ${dashboardTab === "all" ? "active" : ""}`}
                  onClick={() => setDashboardTab("all")}
                >
                  All Time
                </button>
              </div>
            </div>

            {/* Dashboard Inner Content */}
            <div className="dash-window-body">
              {/* Stat Tiles */}
              <div className="dash-metrics-grid">
                <div className="dash-metric-card">
                  <span className="dash-metric-label">Total Revenue</span>
                  <div className="dash-metric-val">&#x20B9;2,45,680</div>
                  <span className="dash-metric-trend positive">&#x2191; +23.5% vs previous</span>
                </div>
                <div className="dash-metric-card">
                  <span className="dash-metric-label">Total Orders</span>
                  <div className="dash-metric-val">1,245</div>
                  <span className="dash-metric-trend positive">&#x2191; +10.2% new buyers</span>
                </div>
                <div className="dash-metric-card">
                  <span className="dash-metric-label">Conversion Rate</span>
                  <div className="dash-metric-val">4.8%</div>
                  <span className="dash-metric-trend positive">&#x2191; +0.9% optimization</span>
                </div>
                <div className="dash-metric-card">
                  <span className="dash-metric-label">Active Listings</span>
                  <div className="dash-metric-val">48 / 50</div>
                  <span className="dash-metric-sub">Pro Plan (0% Commission)</span>
                </div>
              </div>

              {/* Chart & Live Orders Row */}
              <div className="dash-content-grid">
                {/* Visual Sales Chart */}
                <div className="dash-chart-card">
                  <div className="dash-card-header">
                    <h6>Sales Velocity (Weekly Breakdown)</h6>
                    <span className="badge-pro-active">Live Sync</span>
                  </div>
                  <div className="dash-chart-visual">
                    <div className="chart-bar-group">
                      <div className="chart-bar-fill" style={{ height: "45%" }} />
                      <span className="chart-bar-label">Mon</span>
                    </div>
                    <div className="chart-bar-group">
                      <div className="chart-bar-fill" style={{ height: "65%" }} />
                      <span className="chart-bar-label">Tue</span>
                    </div>
                    <div className="chart-bar-group">
                      <div className="chart-bar-fill" style={{ height: "55%" }} />
                      <span className="chart-bar-label">Wed</span>
                    </div>
                    <div className="chart-bar-group">
                      <div className="chart-bar-fill" style={{ height: "80%" }} />
                      <span className="chart-bar-label">Thu</span>
                    </div>
                    <div className="chart-bar-group">
                      <div className="chart-bar-fill" style={{ height: "92%" }} />
                      <span className="chart-bar-label">Fri</span>
                    </div>
                    <div className="chart-bar-group">
                      <div className="chart-bar-fill featured-bar" style={{ height: "100%" }} />
                      <span className="chart-bar-label">Sat</span>
                    </div>
                    <div className="chart-bar-group">
                      <div className="chart-bar-fill" style={{ height: "85%" }} />
                      <span className="chart-bar-label">Sun</span>
                    </div>
                  </div>
                </div>

                {/* Recent Orders Live Feed */}
                <div className="dash-orders-card">
                  <div className="dash-card-header">
                    <h6>Recent Footwear Orders</h6>
                    <Link to="/vendor/register" className="dash-link-more">View All</Link>
                  </div>
                  <div className="dash-orders-table">
                    <div className="dash-order-row">
                      <div className="dash-order-info">
                        <span className="dash-order-id">#ORD-9821</span>
                        <span className="dash-order-item">Air Jordan 1 High (Size 9)</span>
                      </div>
                      <span className="dash-order-amount">&#x20B9;8,499</span>
                      <span className="status-pill active">Delivered</span>
                    </div>

                    <div className="dash-order-row">
                      <div className="dash-order-info">
                        <span className="dash-order-id">#ORD-9820</span>
                        <span className="dash-order-item">Yeezy Boost 350 (Size 8)</span>
                      </div>
                      <span className="dash-order-amount">&#x20B9;7,999</span>
                      <span className="status-pill pending">In Transit</span>
                    </div>

                    <div className="dash-order-row">
                      <div className="dash-order-info">
                        <span className="dash-order-id">#ORD-9819</span>
                        <span className="dash-order-item">Nike Dunk Low Panda (Size 10)</span>
                      </div>
                      <span className="dash-order-amount">&#x20B9;6,499</span>
                      <span className="status-pill pending">Packing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 6. PRICING & FEE CALCULATOR */}
      {/* ========================================================================= */}
      <section id="pricing" className="section-padded bg-subtle-warm">
        <div className="container">
          <div className="section-header-center">
            <span className="section-pill-tag">TRANSPARENT PRICING</span>
            <h2 className="section-title">Simple Plans. Maximum Profit.</h2>
            <p className="section-subtitle">
              Start completely free and upgrade as your footwear business expands. Zero hidden fees.
            </p>
          </div>

          <div className="pricing-cards-grid">
            {/* Basic Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h4 className="pricing-plan-name">Basic</h4>
                <p className="pricing-plan-desc">For new individual footwear sellers testing the waters.</p>
                <div className="pricing-plan-price-wrap">
                  <span className="pricing-plan-price">Free</span>
                </div>
              </div>
              <ul className="pricing-features-list">
                <li><CheckIcon size={16} color="#10b981" /> List up to 5 products</li>
                <li><CheckIcon size={16} color="#10b981" /> 10% commission per sale</li>
                <li><CheckIcon size={16} color="#10b981" /> Standard Seller Center access</li>
                <li><CheckIcon size={16} color="#10b981" /> Email merchant support</li>
                <li><CheckIcon size={16} color="#10b981" /> Nationwide catalog visibility</li>
              </ul>
              <div className="pricing-card-footer">
                <Link to="/vendor/register" className="btn btn-outline-primary w-100">
                  Start Selling Free
                </Link>
              </div>
            </div>

            {/* Growth Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h4 className="pricing-plan-name">Growth</h4>
                <p className="pricing-plan-desc">For active boutique stores with expanding inventory.</p>
                <div className="pricing-plan-price-wrap">
                  <span className="pricing-plan-price">&#x20B9;300</span>
                  <span className="pricing-plan-period">/ month</span>
                </div>
              </div>
              <ul className="pricing-features-list">
                <li><CheckIcon size={16} color="#10b981" /> List up to 20 products</li>
                <li><CheckIcon size={16} color="#10b981" /> 5% commission per sale</li>
                <li><CheckIcon size={16} color="#10b981" /> Priority merchant support</li>
                <li><CheckIcon size={16} color="#10b981" /> Enhanced catalog search placement</li>
                <li><CheckIcon size={16} color="#10b981" /> Store banner customization</li>
              </ul>
              <div className="pricing-card-footer">
                <Link to="/vendor/register" className="btn btn-outline-primary w-100">
                  Choose Growth Plan
                </Link>
              </div>
            </div>

            {/* Pro Plan (Featured) */}
            <div className="pricing-card featured">
              <div className="pricing-popular-badge">
                <SparklesIcon size={14} color="#ffffff" /> MOST POPULAR
              </div>
              <div className="pricing-card-header">
                <h4 className="pricing-plan-name">Pro</h4>
                <p className="pricing-plan-desc">For serious footwear brands looking to maximize margins.</p>
                <div className="pricing-plan-price-wrap">
                  <span className="pricing-plan-price">&#x20B9;499</span>
                  <span className="pricing-plan-period">/ month</span>
                </div>
                <div className="pricing-commission-highlight">
                  &#x2728; 0% Marketplace Commission
                </div>
              </div>
              <ul className="pricing-features-list">
                <li><CheckIcon size={16} color="#10b981" /> <strong>List up to 50 products</strong></li>
                <li><CheckIcon size={16} color="#10b981" /> <strong>0% Marketplace Commission</strong></li>
                <li><CheckIcon size={16} color="#10b981" /> Top-tier product search visibility</li>
                <li><CheckIcon size={16} color="#10b981" /> Dedicated seller account manager</li>
                <li><CheckIcon size={16} color="#10b981" /> Advanced sales &amp; size analytics</li>
                <li><CheckIcon size={16} color="#10b981" /> Early access to new seller features</li>
              </ul>
              <div className="pricing-card-footer">
                <Link to="/vendor/register" className="btn-primary w-100">
                  Go Pro &mdash; 0% Commission &rarr;
                </Link>
              </div>
            </div>
          </div>

          {/* Interactive Calculator Card */}
          <div className="modern-calculator-card">
            <div className="calculator-header">
              <h4 className="calculator-title">See How Much You Save with Pro</h4>
              <p className="calculator-sub">
                Adjust your expected monthly sales to compare fee savings across plans.
              </p>
            </div>

            <div className="calculator-body-grid">
              <div className="calculator-slider-col">
                <label className="form-label d-flex justify-content-between align-items-center">
                  <span>Monthly Footwear Sales</span>
                  <span className="slider-current-val">&#x20B9;{sales.toLocaleString()}</span>
                </label>
                <input
                  type="range"
                  className="form-range"
                  min="5000"
                  max="100000"
                  step="2500"
                  value={sales}
                  onChange={(e) => setSales(Number(e.target.value))}
                />
                <div className="slider-range-labels">
                  <span>&#x20B9;5,000</span>
                  <span>&#x20B9;50,000</span>
                  <span>&#x20B9;1,00,000</span>
                </div>
              </div>

              <div className="calculator-results-col">
                <div className="calc-result-row">
                  <span className="calc-plan-title">Basic Plan (10% Fee)</span>
                  <span className="calc-plan-fee">&#x20B9;{basicFee.toLocaleString()}</span>
                </div>
                <div className="calc-result-row">
                  <span className="calc-plan-title">Growth Plan (&#x20B9;300 + 5%)</span>
                  <span className="calc-plan-fee">&#x20B9;{growthFee.toLocaleString()}</span>
                </div>
                <div className="calc-result-row featured-row">
                  <span className="calc-plan-title">Pro Plan (&#x20B9;499 + 0% Commission)</span>
                  <span className="calc-plan-fee-highlight">&#x20B9;{proFee.toLocaleString()}</span>
                </div>
                {savings > 0 && (
                  <div className="calc-savings-alert">
                    <span>&#x1F4B8;</span>
                    <span>You save <strong>&#x20B9;{savings.toLocaleString()} / month</strong> by choosing the Pro Plan!</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 7. REQUIREMENTS & DOCUMENT VERIFICATION */}
      {/* ========================================================================= */}
      <section id="requirements" className="section-padded bg-white">
        <div className="container">
          <div className="section-header-center">
            <span className="section-pill-tag">SELLER REQUIREMENTS</span>
            <h2 className="section-title">Everything Needed to Get Verified</h2>
            <p className="section-subtitle">
              We keep documentation minimal so you can focus on listing and selling.
            </p>
          </div>

          <div className="requirements-cards-grid">
            <div className="req-card">
              <div className="req-card-icon-box">
                <IdCardIcon size={28} color="#a85050" />
              </div>
              <h4 className="req-card-title">Aadhaar Card</h4>
              <p className="req-card-desc">
                Front and back photo of your Aadhaar card for instant digital identity and business address verification.
              </p>
              <div className="req-card-status">
                <CheckIcon size={14} color="#10b981" /> <span>Mandatory for all sellers</span>
              </div>
            </div>

            <div className="req-card">
              <div className="req-card-icon-box">
                <FileTextIcon size={28} color="#a85050" />
              </div>
              <h4 className="req-card-title">PAN Card</h4>
              <p className="req-card-desc">
                Personal or business PAN card for regulatory compliance and tax settlement reporting.
              </p>
              <div className="req-card-status">
                <CheckIcon size={14} color="#10b981" /> <span>Mandatory for payouts</span>
              </div>
            </div>

            <div className="req-card">
              <div className="req-card-icon-box">
                <CreditCardIcon size={28} color="#a85050" />
              </div>
              <h4 className="req-card-title">Bank Account</h4>
              <p className="req-card-desc">
                Account number and IFSC code for direct, automated payout settlements from customer orders.
              </p>
              <div className="req-card-status">
                <CheckIcon size={14} color="#10b981" /> <span>Direct bank transfer</span>
              </div>
            </div>
          </div>

          <div className="gst-guidance-card">
            <div className="gst-guidance-left">
              <div className="gst-icon-circle">&#x1F4CB;</div>
            </div>
            <div className="gst-guidance-right">
              <h5 className="gst-title">What About GST Registration?</h5>
              <p className="gst-desc">
                GST rules vary based on annual turnover, business classification, and product category. New footwear sellers are not locked out — you can register your store immediately, and our onboarding specialists will guide you through requirements as your volume grows.
              </p>
              <Link to="/vendor/requirements" className="btn-outline-primary btn-sm">
                Read Full Requirements Guide &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 8. FREQUENTLY ASKED QUESTIONS (ACCORDION) */}
      {/* ========================================================================= */}
      <section id="faq" className="section-padded bg-subtle-warm">
        <div className="container" style={{ maxWidth: "880px" }}>
          <div className="section-header-center">
            <span className="section-pill-tag">FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="section-title">Got Questions? We Have Answers.</h2>
            <p className="section-subtitle">
              Everything you need to know about selling sneakers and footwear on IM Online.
            </p>
          </div>

          <div className="modern-faq-container">
            {faqs.map((faq, idx) => (
              <FAQItem
                key={idx}
                q={faq.q}
                a={faq.a}
                isOpen={openFaq === idx}
                onToggle={() => setOpenFaq(openFaq === idx ? null : idx)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 9. FINAL CALL TO ACTION */}
      {/* ========================================================================= */}
      <section className="modern-final-cta-section">
        <div className="container">
          <div className="final-cta-box">
            <div className="final-cta-content">
              <span className="final-cta-pill">START SELLING TODAY</span>
              <h2 className="final-cta-title">Ready to Start Selling Online?</h2>
              <p className="final-cta-desc">
                Create your seller account today and start growing your footwear business with IM Online.
              </p>
              <div className="final-cta-actions">
                <Link to="/vendor/register" className="btn-hero-primary">
                  Start Selling Free <span className="arrow-icon">&rarr;</span>
                </Link>
                <a href="mailto:support@imonline.shop" className="btn-hero-secondary" style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.4)" }}>
                  Talk to Seller Support
                </a>
              </div>
              <div className="final-cta-perks">
                <span>&#x2713; Free to register</span>
                <span>&middot;</span>
                <span>&#x2713; Fast 48h verification</span>
                <span>&middot;</span>
                <span>&#x2713; 0% commission plans available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PublicHome;

